import React from 'react';
import { AccessibilityProps, Dimensions, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';

import { CourseType } from '../api/models/Course';
import { colors, dimensions, fonts, scale } from '../Styles';
import { getCourseColor, getNeededCourseColor } from '../util/CourseColor';

export type ButtonState = 'enabled' | 'disabled' | 'showingActivityIndicator';

type MNOHSButtonProps = AccessibilityProps & {
  /** The title of the button. */
  courseType: CourseType;
  /** Called when the button is pressed. */
  onPress?: () => void;
  /** A style to apply to the button. By default, has `width: '75%'`. */
  style?: ViewStyle;
};

const DoubleUnderline: React.FC<{ topColor: string; bottomColor: string }> = props => (
  <View>
    <View style={[styles.underline, { backgroundColor: props.topColor }]} />
    <View style={[styles.underline, { backgroundColor: props.bottomColor }]} />
  </View>
);

/** MNOHS-styled shared button component. */
export const MNOHSCourseDetailButton = (props: MNOHSButtonProps) => {
  const title = props.courseType.includes('_') ? props.courseType.split('_').join(' ') : props.courseType;
  const topColor = getCourseColor(props.courseType);
  const bottomColor = getNeededCourseColor(props.courseType);

  return (
    <View style={[styles.container, props.style]}>
      <TouchableOpacity
        onPress={props.onPress}
        style={{ width: '100%' }}
        activeOpacity={0.75}
        accessibilityLabel={props.accessibilityLabel}>
        <Text style={styles.title}>{title}</Text>
        <DoubleUnderline topColor={topColor} bottomColor={bottomColor} />
      </TouchableOpacity>
    </View>
  );
};

const { width } = Dimensions.get('window');

const _getCardWidth = () => {
  if (width <= 320) {
    // devices 320 down get 3 columns
    // 54: 12 outer horizontal margin * 2 = 24
    // and 5 margin each card, multiplied by 6 (each vertical edge of card) = 30
    return (width - 54) / 3;
  } else if (width >= dimensions.WEB_MAX_CONTAINER_WIDTH) {
    // devices that exceed the max container width for web
    // 64: 12 outer horizontal margin * 2 = 24
    // and 5 margin each card, multiplied by 8 (each vertical edge of card) = 40
    return (dimensions.WEB_MAX_CONTAINER_WIDTH - 64) / 4;
  } else {
    // devices in between also get 4 columns
    // 64: 12 outer horizontal margin * 2 = 24
    // and 5 margin each card, multiplied by 8 (each vertical edge of card) = 40
    return (width - 64) / 4;
  }
};

const styles = StyleSheet.create({
  container: {
    width: scale(_getCardWidth()),
    borderRadius: scale(8),
    backgroundColor: colors.grayLight,
    borderColor: colors.grayLight,
    borderWidth: scale(1), // iOS Shadowing
    shadowColor: colors.black,
    shadowRadius: 2,
    shadowOpacity: 0.5,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    // Android Shadowing
    elevation: 5,
    alignItems: 'center',
    justifyContent: 'flex-end',
    margin: scale(5),
    height: scale(100),
    paddingHorizontal: scale(3),
    paddingBottom: scale(20),
  },
  title: {
    ...fonts.smallBold,
    paddingTop: scale(15),
    paddingBottom: scale(5),
    color: colors.black,
    textAlign: 'center',
  },
  underline: {
    width: '60%',
    height: scale(6),
    alignSelf: 'center',
    marginBottom: '5%',
  },
});
